import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Popconfirm,
  message,
  Spin,
  Tag,
} from "antd";

import * as Icons from "@ant-design/icons";
import {
  getTunjangans,
  createTunjangan,
  updateTunjangan,
  deleteTunjangan,
} from "../../api/master/MasterTunjangan";

const { Option } = Select;

const MasterTunjangan = () => {
  const sessiData = JSON.parse(sessionStorage.getItem("user"));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Cari ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<Icons.SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Cari
        </Button>
        <Button
          onClick={() => {
            handleReset(clearFilters);
            confirm({ closeDropdown: true });
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <Icons.SearchOutlined
        style={{ color: filtered ? "#1890ff" : undefined }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <span style={{ backgroundColor: "#ffc069", padding: 0 }}>{text}</span>
      ) : (
        text
      ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
    setSearchedColumn("");
  };

  const fetchTunjangans = async () => {
    setLoading(true);
    try {
      const response = await getTunjangans();
      if (response.data.status && Array.isArray(response.data.data)) {
        setData(response.data.data);
      } else {
        setData([]);
        message.warning("Data kosong atau tidak sesuai format");
      }
    } catch (error) {
      message.error("Gagal memuat data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTunjangans();
  }, []);

  const showForm = (record = null) => {
    setEditingRecord(record);
    form.resetFields();
    if (record) {
      form.setFieldsValue({
        nama: record.tnj_nama_tunjangan,
        keterangan: record.tnj_keterangan,
        status: record.tnj_status,
      });
    }
    setFormVisible(true);
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editingRecord) {
        await updateTunjangan(editingRecord.tnj_id, {
          tnj_nama_tunjangan: values.nama,
          tnj_keterangan: values.keterangan,
          tnj_status: values.status,
          tnj_update_by: sessiData[0].user[0].fullname,
        });
        message.success("Data berhasil diperbarui");
      } else {
        await createTunjangan({
          tnj_nama_tunjangan: values.nama,
          tnj_keterangan: values.keterangan,
          tnj_status: values.status,
          tnj_created_by: sessiData[0].user[0].fullname,
        });
        message.success("Data berhasil ditambahkan");
      }
      setFormVisible(false);
      fetchTunjangans();
    } catch (error) {
      message.error("Gagal menyimpan data");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await deleteTunjangan(id);
      message.success("Data berhasil dihapus");
      fetchTunjangans();
    } catch (error) {
      message.error("Gagal menghapus data");
    }
    setLoading(false);
  };

  const columns = [
    {
      title: "Nama Tunjangan",
      dataIndex: "tnj_nama_tunjangan",
      key: "tnj_nama_tunjangan",
      ...getColumnSearchProps("tnj_nama_tunjangan"),
    },
    {
      title: "Keterangan",
      dataIndex: "tnj_keterangan",
      key: "keterangan",
    },
    {
      title: "Status",
      dataIndex: "tnj_status",
      key: "status",
      width: 100,
      filters: [
        { text: "Aktif", value: "Aktif" },
        { text: "Tidak Aktif", value: "Tidak Aktif" },
      ],
      onFilter: (value, record) => record.tnj_status === value,
      render: (tnj_status) =>
        tnj_status === "Aktif" ? (
          <Tag color="green">Aktif</Tag>
        ) : (
          <Tag color="red">Tidak Aktif</Tag>
        ),
    },
    {
      title: "Aksi",
      key: "aksi",
      width: 50,
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => showForm(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Yakin ingin menghapus data ini?"
            onConfirm={() => handleDelete(record.tnj_id)}
          >
            <Button type="link" danger>
              Hapus
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={() => showForm()}>
          Tambah Tunjangan
        </Button>
      </Space>

      <Spin spinning={loading}>
        <Table
          rowKey="tnj_id"
          columns={columns}
          dataSource={Array.isArray(data) ? data : []}
          pagination={{
            pageSizeOptions: ["10", "20", "40", "60"],
            showSizeChanger: true,
            defaultPageSize: 20,
          }}
        />
      </Spin>

      <Modal
        open={formVisible}
        title={editingRecord ? "Edit Tunjangan" : "Tambah Tunjangan"}
        onCancel={() => setFormVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Nama Tunjangan"
            name="nama"
            rules={[{ required: true, message: "Nama tunjangan wajib diisi" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Keterangan" name="keterangan">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: "Status wajib dipilih" }]}
          >
            <Select placeholder="Pilih status">
              <Option value="Aktif">Aktif</Option>
              <Option value="Tidak Aktif">Tidak Aktif</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setFormVisible(false)}>Batal</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                Simpan
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MasterTunjangan;
